import {useEffect} from 'react';
import {observer} from 'mobx-react';
import {FormattedMessage} from 'react-intl';
import {Redirect} from 'react-router-dom';
import {withState} from 'shared/core';
import useModal from 'shared/hooks/useModal';
import {Button, Display, Layout, Panel} from 'shared/components';
import DragAndDropContext from 'shared/components/dnd/DragAndDropContext';
import {TimeTrackingSubnav} from 'main/containers/time_tracking';
import {Shift} from 'main/stores/time_tracking';
import ShiftSchedulerState from 'main/containers/time_tracking/components/shifts/state/ShiftSchedulerState';
import ShiftGrid from './ShiftGrid';
import ScheduleLegend from './ScheduleLegend';
import EditShiftModal from './EditShiftModal';
import CopyEmployeeShiftsModal from './CopyEmployeeShiftsModal';
import MissingDimensionsModal from './MissingDimensionsModal';
import RepeatScopeModal from './RepeatScopeModal';
import PublishConfirmationModal from './PublishConfirmationModal';
import PublishedShiftChangeModal from './PublishedShiftChangeModal';

const WeekNavigation = observer(({uiState}) => {
  return (
    <div className='flex align-items-middle'>
      <Button size='sm' trait='default' onClick={() => uiState.goToPreviousWeek()}>
        <i className='material-symbols-solid align-middle'>{'chevron_left'}</i>
      </Button>
      <div className='h3 medium px2'>{uiState.weekRange}</div>
      <Button size='sm' trait='default' onClick={() => uiState.goToNextWeek()}>
        <i className='material-symbols-solid align-middle'>{'chevron_right'}</i>
      </Button>
      <Button className='ml2' size='sm' trait='default' onClick={() => uiState.goToCurrentWeek()}>
        <FormattedMessage id='time_tracking.shifts.This Week'/>
      </Button>
    </div>
  );
});

const PublishScheduleButton = observer(({uiState}) => {
  const publishableCount = uiState.publishableWeekShifts.length;
  if (!uiState.canPublish) return null;

  return (
    <Button size='sm' disabled={publishableCount === 0 || uiState.isUpdating} onClick={() => uiState.requestPublish()}>
      <FormattedMessage id='time_tracking.shifts.PUBLISH_SCHEDULE' values={{count: publishableCount}}/>
    </Button>
  );
});

const SchedulerActions = observer(({uiState}) => {
  return (
    <>
      <Button size='sm' trait='default' disabled={uiState.isUpdating} onClick={() => uiState.copyPreviousWeek()}>
        <FormattedMessage id='time_tracking.shifts.Copy Previous Week'/>
      </Button>
      <PublishScheduleButton uiState={uiState}/>
    </>
  );
});

const useCopyModifierTracking = (uiState) => {
  useEffect(() => {
    // macOS browsers report Command on the mousedown and dragstart that begin a drag, but not reliably on
    // the dragover events during it, so once Command is seen it is latched for the rest of the drag.
    let commandHeld = false;

    const updateCopyOnDrop = e => {
      commandHeld = commandHeld || e.metaKey;
      uiState.copyOnDrop = e.altKey || e.ctrlKey || commandHeld;
    };
    const handleMouseDown = e => {
      commandHeld = e.metaKey;
    };
    const handleDragOver = e => {
      updateCopyOnDrop(e);
      if (!e.target.closest('.ShiftGrid-cell')) uiState.hoveredCell = null;
    };

    window.addEventListener('mousedown', handleMouseDown, true);
    window.addEventListener('dragstart', updateCopyOnDrop, true);
    window.addEventListener('dragover', handleDragOver, true);

    return () => {
      window.removeEventListener('mousedown', handleMouseDown, true);
      window.removeEventListener('dragstart', updateCopyOnDrop, true);
      window.removeEventListener('dragover', handleDragOver, true);
    };
  }, [uiState]);
};

const SchedulerHeader = observer(({uiState, scope}) => {
  if (uiState.hasScheduleDirectory) {
    return <Display backPath='/schedules' title={uiState.schedule.name}/>;
  }

  return <TimeTrackingSubnav scope={scope}/>;
});

const ShiftScheduler = observer(({uiState, scope}) => {
  const editShiftModalOpen = useModal(uiState, 'EditShiftModal', 'editingShift', Shift, 'errors');
  const repeatScopeModalOpen = useModal(uiState, 'RepeatScopeModal', 'repeatScopeRequest');
  const copyEmployeeShiftsModalOpen = useModal(uiState, 'CopyEmployeeShiftsModal', 'copyingFromEmployee', null, 'copyErrors');
  const missingDimensionsModalOpen = useModal(uiState, 'MissingDimensionsModal', 'missingAssignments');
  const publishConfirmationModalOpen = useModal(uiState, 'PublishConfirmationModal', 'publishRequest');
  const publishedShiftChangeModalOpen = useModal(uiState, 'PublishedShiftChangeModal', 'publishedShiftChangeRequest');

  useCopyModifierTracking(uiState);

  if (!uiState.schedule) return <Redirect to='/schedules'/>;

  return (
    <div>
      <SchedulerHeader uiState={uiState} scope={scope}/>
      <Layout>
        <Panel>
          <Panel.Header title={<WeekNavigation uiState={uiState}/>}>
            <SchedulerActions uiState={uiState}/>
          </Panel.Header>
          <ScheduleLegend/>
          <ShiftGrid uiState={uiState}/>
        </Panel>
        <EditShiftModal uiState={uiState} isOpen={editShiftModalOpen}/>
        <RepeatScopeModal uiState={uiState} isOpen={repeatScopeModalOpen}/>
        <CopyEmployeeShiftsModal
          isOpen={copyEmployeeShiftsModalOpen}
          employee={uiState.copyingFromEmployee}
          shifts={uiState.copyingFromEmployeeShifts}
          employees={uiState.copyableEmployees}
          week={uiState.weekRange}
          errors={uiState.copyErrors}
          onCopy={targetEmployees => uiState.copyEmployeeShifts(targetEmployees)}
          onClose={() => uiState.closeCopyEmployeeShiftsModal()}
        />
        <MissingDimensionsModal uiState={uiState} isOpen={missingDimensionsModalOpen}/>
        <PublishConfirmationModal uiState={uiState} isOpen={publishConfirmationModalOpen}/>
        <PublishedShiftChangeModal uiState={uiState} isOpen={publishedShiftChangeModalOpen}/>
      </Layout>
    </div>
  );
});

export default withState(DragAndDropContext(ShiftScheduler), ShiftSchedulerState);
